import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const CartItem = ({ item }) => {
  const { updateQuantity, removeFromCart } = useCart();

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item.id);
    } else {
      updateQuantity(item.id, newQuantity);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 py-4 sm:py-6 border-b border-gray-200">
      {/* Mobile Layout */}
      <div className="flex items-start space-x-3 sm:hidden">
        <Link to={`/product/${item.id}`}>
          <img 
            src={item.image} 
            alt={item.name}
            className="w-16 h-16 object-cover rounded-lg hover:opacity-80 transition-opacity flex-shrink-0"
          />
        </Link>

        <div className="flex-1 min-w-0">
          <Link 
            to={`/product/${item.id}`}
            className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors line-clamp-2"
          >
            {item.name}
          </Link>
          <p className="text-xs text-gray-600 mt-1">{item.category}</p>
          
          {/* Product Attributes */}
          <div className="flex flex-wrap gap-1 mt-1">
            {item.selectedColor && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {item.selectedColor}
              </span>
            )}
            {item.selectedSize && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {item.selectedSize}
              </span>
            )}
          </div>
          <p className="text-xs text-blue-600 font-medium mt-1">
            ${item.price.toFixed(2)} each
          </p>
        </div>

        <button
          onClick={() => removeFromCart(item.id)}
          className="text-red-600 hover:text-red-800 p-1 touch-optimized"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Mobile Quantity and Price */}
      <div className="flex items-center justify-between sm:hidden">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleQuantityChange(item.quantity - 1)}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors touch-optimized"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <span className="w-8 text-center font-medium text-base">
            {item.quantity}
          </span>

          <button
            onClick={() => handleQuantityChange(item.quantity + 1)}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors touch-optimized"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        <div className="text-right">
          <p className="text-lg font-semibold text-gray-900">
            ${(item.price * item.quantity).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Desktop Layout */}
      <Link to={`/product/${item.id}`} className="hidden sm:block">
        <img 
          src={item.image} 
          alt={item.name}
          className="w-20 h-20 object-cover rounded-lg hover:opacity-80 transition-opacity"
        />
      </Link>

      <div className="flex-1 min-w-0 hidden sm:block">
        <Link 
          to={`/product/${item.id}`}
          className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors"
        >
          {item.name}
        </Link>
        <p className="text-sm text-gray-600 mt-1">{item.category}</p>
        
        {/* Product Attributes */}
        <div className="flex flex-wrap gap-2 mt-2">
          {item.selectedColor && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {item.selectedColor}
            </span>
          )}
          {item.selectedSize && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {item.selectedSize}
            </span>
          )}
        </div>
        <p className="text-sm text-blue-600 font-medium mt-1">
          ${item.price.toFixed(2)} each
        </p>
      </div>

      <div className="flex items-center space-x-3 hidden sm:flex">
        <button
          onClick={() => handleQuantityChange(item.quantity - 1)}
          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>

        <span className="w-12 text-center font-medium text-lg">
          {item.quantity}
        </span>

        <button
          onClick={() => handleQuantityChange(item.quantity + 1)}
          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      <div className="text-right hidden sm:block">
        <p className="text-lg font-semibold text-gray-900">
          ${(item.price * item.quantity).toFixed(2)}
        </p>
        <button
          onClick={() => removeFromCart(item.id)}
          className="text-red-600 hover:text-red-800 text-sm mt-1 transition-colors"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default CartItem;